<template>
    <v-card :elevation="elevation">
        <v-card-title v-if="header || filter">
            <slot name="header">
                <h1 v-if="header">{{ header }}</h1>
            </slot>
            <v-spacer v-if="!$vuetify.breakpoint.xs && filter"></v-spacer>
            <v-text-field
                    :disabled="loadingState"
                    clearable
                    append-icon="search"
                    label="Filter"
                    single-line
                    hide-details
                    v-model="search"
                    v-if="!$vuetify.breakpoint.xs && filter"
            ></v-text-field>
        </v-card-title>

        <!-- Desktop -->
        <v-data-table :headers="dataTableConfig.headers"
                      :items="itemList"
                      :search="search"
                      :rows-per-page-text="dataTableConfig.rowsPerPageText"
                      :rows-per-page-items="[-1]"
                      :no-results-text="$t('no_results')"
                      :loading="loadingState"
                      :pagination.sync="pagination"
                      v-model="selectedItems"
                      select-all
                      hide-actions
                      class="hidden-xs-only"
        >
            <template slot="headers" slot-scope="props">
                <tr>
                    <th v-if="selectable" class="pr-0">
                        <v-checkbox
                                :disabled="loadingState"
                                :input-value="props.all"
                                :indeterminate="props.indeterminate"
                                primary
                                hide-details
                                @click.native="selectable && !(loadingState) ? toggleAll() : null"
                        ></v-checkbox>
                    </th>
                    <th
                            v-for="header in props.headers"
                            :key="header.text"
                            :class="['column sortable text-xs-left', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
                            @click="changeSort(header.value)"
                    >
                        {{ header.text }}
                        <v-icon small>arrow_upward</v-icon>
                    </th>
                    <th v-if="showActionCol"></th>
                </tr>
            </template>
            <v-progress-linear v-if="showProgress" slot="progress" color="blue" v-model="progress"></v-progress-linear>
            <template slot="items" slot-scope="props">
                <tr :active="props.selected" @click="selectable && !(loadingState) ? props.selected = !props.selected : null">
                    <td v-if="selectable" class="pr-0">
                        <v-checkbox
                                :disabled="loadingState"
                                :input-value="props.selected"
                                primary
                                hide-details
                        ></v-checkbox>
                    </td>

                    <td
                        v-for="item in dataTableConfig.items.large"
                        :class="itemClasses(props.item, item.class)"
                    >
                        {{ itemValue(props, item) }}
                    </td>

                    <td v-if="showActionCol && isDeleted(props.item)" class="text-xs-right">
                        <v-tooltip top>
                            <v-icon slot="activator" style="margin-bottom: -5px;">info</v-icon>
                            <span>Meldung storniert</span>
                        </v-tooltip>
                    </td>

                    <td v-if="showActionCol && !isDeleted(props.item)" class="text-xs-right" style="white-space: nowrap">
                        <template v-if="!actionsFirst">
                            <v-tooltip top
                                       v-for="tooltip in tooltips" :key="tooltip.label" :data="tooltip"
                                       v-if="tooltip.visible === undefined || (tooltip.visible !== undefined && tooltip.visible(props.item))"
                            >
                                <v-icon slot="activator" style="margin-bottom: -5px;" :color="tooltip.color">{{ tooltip.icon }}</v-icon>
                                <span>{{ tooltip.label }}</span>
                            </v-tooltip>
                        </template>


                        <v-tooltip top v-for="action in actions" :key="action.id" :data="action"
                                   v-if="action.visible === undefined || (action.visible !== undefined && action.visible(props.item))"
                        >
                            <v-btn icon
                                   slot="activator"
                                   @click="action.click(props.item)"
                                   :loading="action.loading !== undefined && action.loading(props.item)"
                                   :disabled="lockActions || (action.disabled !== undefined && action.disabled(props.item))"
                                   class="mx-0">
                                <v-icon>{{ action.icon }}</v-icon>
                            </v-btn>
                            <span>{{ action.label }}</span>
                        </v-tooltip>

                        <template v-if="actionsFirst">
                            <v-tooltip top
                                       v-for="tooltip in tooltips" :key="tooltip.label" :data="tooltip"
                                       v-if="tooltip.visible === undefined || (tooltip.visible !== undefined && tooltip.visible(props.item))"
                            >
                                <v-icon slot="activator" style="margin-bottom: -5px;" :color="tooltip.color">{{ tooltip.icon }}</v-icon>
                                <span>{{ tooltip.label }}</span>
                            </v-tooltip>
                        </template>
                    </td>
                </tr>
            </template>
            <template slot="pageText" slot-scope="{ pageStart, pageStop, itemsLength }">
                {{ pageStart }} bis {{ pageStop }} von {{ itemsLength }}
            </template>
            <template slot="no-data">
                {{ !items.loaded ? dataTableConfig.loadingDataText : dataTableConfig.noDataText }}
            </template>
        </v-data-table>

        <!-- Mobile -->
        <v-data-table :headers="dataTableConfig.headers"
                      :items="itemList"
                      :search="search"
                      :no-data-text="dataTableConfig.noDataText"
                      :rows-per-page-text="dataTableConfig.rowsPerPageText"
                      :rows-per-page-items="[-1]"
                      :no-results-text="$t('no_results')"
                      :loading="loadingState"
                      :pagination.sync="pagination"
                      v-model="selectedItems"
                      select-all
                      hide-actions
                      class="hidden-sm-and-up"
        >
            <template slot="headers" slot-scope="props">
                <tr>
                    <th v-if="selectable" class="pr-0">
                        <v-checkbox
                                :disabled="loadingState"
                                :input-value="props.all"
                                :indeterminate="props.indeterminate"
                                primary
                                hide-details
                                @click.native="selectable && !(loadingState) ? toggleAll() : null"
                        ></v-checkbox>
                    </th>
                    <th
                            v-for="header in mobileHeaders(props.headers)"
                            :key="header.text"
                            :class="['column sortable text-xs-left', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
                            @click="changeSort(header.value)"
                    >
                        {{ header.text }}
                        <v-icon small>arrow_upward</v-icon>
                    </th>
                    <th v-if="showActions"></th>
                </tr>
            </template>
            <v-progress-linear v-if="showProgress" slot="progress" color="blue" v-model="progress"></v-progress-linear>
            <template slot="items" slot-scope="props">
                <tr :active="props.selected" @click="selectable && !(loadingState) ? props.selected = !props.selected : null">
                    <td v-if="selectable" class="pr-0">
                        <v-checkbox
                                :disabled="loadingState"
                                :input-value="props.selected"
                                primary
                                hide-details
                        ></v-checkbox>
                    </td>

                    <td v-for="item in dataTableConfig.items.small"
                        :class="{'deleted': props.item.hasOwnProperty('deleted_at') && !!props.item.deleted_at}"
                    >
                        {{ itemValue(props, item) }}
                    </td>

                    <td class="text-xs-right" v-if="showActions">
                        <v-btn icon v-if="visibleActions" @click="sheet = true; selectedItem = props.item" class="mx-0">
                            <v-icon>more_vert</v-icon>
                        </v-btn>
                    </td>
                </tr>
            </template>
            <template slot="pageText" slot-scope="{ pageStart, pageStop }">
                {{ pageStart }} bis {{ pageStop }}
            </template>
        </v-data-table>

        <v-bottom-sheet v-model="sheet">
            <v-list>
                <!--<v-subheader>Aktion</v-subheader>-->
                <v-list-tile
                        v-for="action in actions"
                        :key="action.label"
                        v-if="action.visible === undefined || (selectedItem !== null && action.visible !== undefined && action.visible(selectedItem))"
                        @click="sheet = false; action.click(selectedItem)"
                        :disabled="lockActions || (selectedItem !== null && action.disabled !== undefined && action.disabled(selectedItem))"
                >
                    <!-- :loading="selectedItem !== null && action.loading !== undefined && action.loading(selectedItem)" -->
                    <v-list-tile-avatar>
                        <v-avatar size="32px" tile>
                            <v-icon v-if="action.icon">{{ action.icon }}</v-icon>
                        </v-avatar>
                    </v-list-tile-avatar>
                    <v-list-tile-title>{{ action.label }}</v-list-tile-title>
                </v-list-tile>
            </v-list>
        </v-bottom-sheet>
    </v-card>
</template>

<script>
    export default {
        props: {
            items: {
                type: [Object, Array],
                default: () => ({})
            },
            dataTableConfig: {
                type: Object,
                default: null
            },
            actions: {
                type: Array,
                default: () => ([])
            },
            tooltips: {
                type: Array,
                default: () => ([])
            },
            header: {
                type: String,
                default: null
            },
            lockActions: {
                type: Boolean,
                default: false
            },
            selectable: {
                type: Boolean,
                default: false
            },
            selected: {
                type: Array,
                default: null
            },
            showActions: {
                type: Boolean,
                default: false
            },
            loading: {
                type: Boolean,
                default: false
            },
            filter: {
                type: Boolean,
                default: true
            },
            elevation: {
                type: [String, Number],
                default: 2
            },
            actionsFirst: {
                type: Boolean,
                default: false
            },
            showProgress: {
                type: Boolean,
                default: false
            },
            progress: {
                type: Number,
                default: 0
            }
        },
        watch: {
            selectedItems() {
                this.$emit('update:selected', this.selectedItems);
            },
            selected() {
                this.$set(this, 'selectedItems', this.selected);
            }
        },
        data() {
            return {
                search: '',
                sheet: false,
                pagination: {
                    sortBy: '',
                    descending: false
                },
                selectedItems: [],
                headers: this.dataTableConfig.headers,
                selectedItem: null
            }
        },
        computed: {
            itemList() {
                return Array.isArray(this.items) ? this.items : this.items.items;
            },
            loadingState() {
                return this.showProgress || (!Array.isArray(this.items) ? !this.items.loaded || this.loading : this.loading);
            },
            actionsWidth() {
                return "width: "  + (60 * (this.actions.length + this.tooltips.length)) + "px;";
            },
            visibleActions() {
                return this.showActions && this.actions.length > 0 && _.some(this.actions, a => {
                    return a.visible === undefined || _.some(this.itemList, i => a.visible(i));
                });
            },
            visibleTooltips() {
                return this.tooltips.length > 0 && _.some(this.tooltips, t => {
                    return t.visible === undefined || _.some(this.itemList, i => t.visible(i));
                });
            },
            showActionCol() {
                return this.visibleActions || this.visibleTooltips;
            }
        },
        methods: {
            itemValue(props, item) {
                let value = null;

                //extract nested values with .
                if (item.value.includes('.')) {
                    let keys = item.value.split('.');
                    let tempValue = props.item;
                    let isUndefined = false;

                    _.each(keys, key => {
                        if (tempValue === null || tempValue[key] === undefined) {
                            isUndefined = true;
                            return false;
                        }
                        tempValue = tempValue[key];
                    });

                    if (isUndefined) {
                        if (item.default !== undefined && item.default) {
                            return item.default;
                        }

                        return null;
                    }

                    value = tempValue;
                } else {
                    value = props.item[item.value]
                }

                if (value === null || value === '') {
                    if (item.default !== undefined && item.default) {
                        return item.default;
                    }

                    return null;
                }

                if (item.translate !== undefined && item.translate) {
                    return this.$t(value);
                }
                if (item.formatFunction !== undefined && item.formatFunction) {
                    if (Array.isArray(item.formatFunction)) {
                        let val = value;
                        _.each(item.formatFunction, f => {
                            val = this[f](val);
                        });
                        return val;
                    } else {
                        return this[item.formatFunction](value);
                    }
                }

                return value;
            },
            isActionsHeader(header) {
                return header.text === 'Aktion';
            },
            mobileHeaders(headers) {
                return headers.filter(header => {
                    if (this.isActionsHeader(header)) {
                        return true;
                    }

                    for (let i = 0; i < this.dataTableConfig.items.small.length; ++i) {
                        if (header.value === this.dataTableConfig.items.small[i].value) {
                            return true;
                        }
                    }

                    return false;
                });
            },
            toggleAll() {
                let items = this.items;
                if (items.hasOwnProperty('items')) {
                    items = items.items;
                }
                this.$set(this, 'selectedItems', this.selectedItems.length ? [] : items);
            },
            changeSort (column) {
                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }
            },
            itemClasses(item, classes) {
                if (classes === undefined) {
                    classes = [];
                }

                if (this.isDeleted(item)) {
                    classes.push('deleted');
                }

                return classes;
            },
            isDeleted(item) {
                return (item.hasOwnProperty('deleted_at') && !!item.deleted_at) || item.canceled;
            }
        }
    }
</script>

<style scoped>
    td.deleted {
        color: gray;
        text-decoration: line-through;
    }
</style>